import { useRef, useState } from "react";
import { CLIENTS } from "../data/clients";
import { useClientBrand, effectiveBrand, type BrandOverride } from "../store/clientBrand";
import { useSettings } from "../store/settings";
import { extractPalette } from "../lib/colorExtract";
import { generateBrandTextAI } from "../lib/claude";
import { generateImageGemini, generateImageOpenAI } from "../lib/generators";
import { resizeForLogo, resizeForSpokesperson } from "../lib/imageResize";
import { logoPrompt, spokespersonPrompt } from "../lib/promptTemplates";

export function BrandKitEditor({ clientId, onClose }:
  { clientId: string; onClose: () => void }) {

  const client = CLIENTS.find(c => c.id === clientId);
  const ov = useClientBrand(s => s.overrides[clientId]) ?? {};
  const setOv = useClientBrand(s => s.set);
  const clearField = useClientBrand(s => s.clearField);
  const settings = useSettings();

  const [loading, setLoading] = useState<string | null>(null);
  const [hint, setHint] = useState("");

  const logoInput = useRef<HTMLInputElement>(null);
  const spokesInput = useRef<HTMLInputElement>(null);

  if (!client) return null;

  const eff = effectiveBrand(client, ov);
  const patch = (p: BrandOverride) => setOv(clientId, p);

  const readFile = (f: File, cb: (d: string) => void) => {
    if (f.size > 16 * 1024 * 1024) { alert("Imagem > 16MB."); return; }
    const r = new FileReader();
    r.onload = () => cb(String(r.result));
    r.readAsDataURL(f);
  };

  const setLogoSafe = async (raw: string) => {
    try {
      const small = await resizeForLogo(raw);
      patch({ logoUrl: small });
    } catch { patch({ logoUrl: raw }); }
  };
  const setSpokesSafe = async (raw: string) => {
    try {
      const small = await resizeForSpokesperson(raw);
      patch({ spokespersonUrl: small });
    } catch { patch({ spokespersonUrl: raw }); }
  };

  const extractFromLogo = async () => {
    const url = eff.logoUrl;
    if (!url) { alert("Faça upload do logo primeiro."); return; }
    setLoading("extract");
    try {
      const p = await extractPalette(url);
      patch({ primary: p.primary, secondary: p.secondary, accent: p.accent });
    } catch (e) {
      alert("Falha na extração: " + (e as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const genText = async (field: "persona" | "audience" | "font" | "voiceTone" | "brief" | "pitch" | "challenge" | "tags") => {
    if (!settings.anthropicKey) { alert("Configure a chave Anthropic em ⚙️."); return; }
    setLoading(field);
    try {
      const text = await generateBrandTextAI({
        apiKey: settings.anthropicKey,
        model: settings.model,
        field,
        clientName: client.name,
        clientPitch: client.pitch,
        segment: client.segment,
        currentAudience: eff.audience,
        hint
      });
      patch({ [field]: text } as any);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const genSpokesperson = async () => {
    const provider = settings.imageProvider;
    const key   = provider === "gemini" ? settings.geminiKey : settings.openaiKey;
    const model = provider === "gemini" ? settings.geminiImageModel : settings.openaiImageModel;
    if (!key) { alert(`Configure a chave ${provider} em ⚙️.`); return; }
    setLoading("spokes");
    try {
      const prompt = spokespersonPrompt({
        clientName: client.name,
        segment: client.segment,
        persona: eff.persona,
        audience: eff.audience,
        brand: { primary: eff.primary, secondary: eff.secondary, accent: eff.accent }
      });
      const fn = provider === "gemini" ? generateImageGemini : generateImageOpenAI;
      const out = await fn({ apiKey: key, model, prompt, aspect: "1:1" });
      await setSpokesSafe(out.dataUrl);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const genLogo = async () => {
    const provider = settings.imageProvider;
    const key   = provider === "gemini" ? settings.geminiKey : settings.openaiKey;
    const model = provider === "gemini" ? settings.geminiImageModel : settings.openaiImageModel;
    if (!key) { alert(`Configure a chave ${provider} em ⚙️.`); return; }
    setLoading("logo");
    try {
      const prompt = logoPrompt({
        clientName: client.name,
        segment: client.segment,
        pitch: eff.pitch,
        brand: { primary: eff.primary, secondary: eff.secondary, accent: eff.accent }
      });
      const fn = provider === "gemini" ? generateImageGemini : generateImageOpenAI;
      const out = await fn({ apiKey: key, model, prompt, aspect: "1:1" });
      await setLogoSafe(out.dataUrl);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-5xl max-h-[92vh] overflow-y-auto
                      shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between gap-3"
                style={{ background: eff.primary, color: eff.accent }}>
          <div className="flex items-center gap-3 min-w-0">
            {eff.logoUrl && (
              <div className="w-12 h-12 bg-bone p-1 flex items-center justify-center shrink-0">
                <img src={eff.logoUrl} alt="" className="max-w-full max-h-full object-contain" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-mono text-xs opacity-80">BRAND KIT · {client.id.toUpperCase()}</div>
              <h2 className="font-display font-black uppercase text-2xl leading-none truncate">{client.name}</h2>
              <div className="font-crimson italic text-sm opacity-80">{client.segment}</div>
            </div>
          </div>
          <button onClick={onClose} className="btn bg-bone text-ink border-bone shrink-0">fechar ×</button>
        </header>

        <div className="p-5 space-y-5">
          {/* hint global */}
          <div className="border border-ink p-3 bg-gold/10">
            <div className="tag text-graydk mb-1">observação opcional pro Claude (vale pra TODA geração de texto)</div>
            <input
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="ex: foco em famílias premium, evitar luxo ostentoso, tom técnico…"
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]"
            />
          </div>

          {/* LOGO + GAROTO PROPAGANDA lado a lado */}
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Logotipo" actionable>
              <div className="flex items-center gap-3">
                <Preview src={eff.logoUrl} placeholder="sem logo" />
                <div className="flex flex-col gap-1 flex-1">
                  <button onClick={() => logoInput.current?.click()} className="btn btn-fire">
                    {eff.logoUrl ? "trocar" : "upload"} png/svg
                  </button>
                  <button onClick={genLogo} disabled={loading === "logo"} className="btn btn-fire">
                    {loading === "logo" ? "gerando…" : "✦ gerar com IA"}
                  </button>
                  {eff.logoUrl && (
                    <a href={eff.logoUrl} download={`logo-${client.id}.png`} className="btn btn-fire text-center">
                      ↓ baixar
                    </a>
                  )}
                  {ov.logoUrl && (
                    <button onClick={() => clearField(clientId, "logoUrl")} className="tag text-red underline">
                      remover override
                    </button>
                  )}
                </div>
                <input ref={logoInput} type="file" accept="image/*" className="hidden"
                       onChange={(e) => {
                         const f = e.target.files?.[0]; if (f) readFile(f, (d) => setLogoSafe(d));
                         if (e.target) e.target.value = "";
                       }} />
              </div>
              {eff.logoUrl && (
                <button onClick={extractFromLogo} disabled={loading === "extract"}
                        className="btn bg-ink text-bone border-ink mt-3 w-full disabled:opacity-50">
                  {loading === "extract" ? "extraindo cores…" : "🎨 extrair paleta do logo"}
                </button>
              )}
            </Section>

            <Section title="Garoto / Garota propaganda" actionable>
              <div className="flex items-center gap-3">
                <Preview src={eff.spokespersonUrl} placeholder="sem foto" />
                <div className="flex flex-col gap-1 flex-1">
                  <button onClick={() => spokesInput.current?.click()} className="btn btn-fire">
                    upload foto
                  </button>
                  <button onClick={genSpokesperson} disabled={loading === "spokes"} className="btn btn-fire">
                    {loading === "spokes" ? "gerando…" : "✦ gerar com IA"}
                  </button>
                  {eff.spokespersonUrl && (
                    <a href={eff.spokespersonUrl} download={`spokesperson-${client.id}.png`} className="btn btn-fire text-center">
                      ↓ baixar
                    </a>
                  )}
                  {ov.spokespersonUrl && (
                    <button onClick={() => clearField(clientId, "spokespersonUrl")} className="tag text-red underline">
                      remover
                    </button>
                  )}
                </div>
                <input ref={spokesInput} type="file" accept="image/*" className="hidden"
                       onChange={(e) => {
                         const f = e.target.files?.[0]; if (f) readFile(f, (d) => setSpokesSafe(d));
                         if (e.target) e.target.value = "";
                       }} />
              </div>
              <p className="font-crimson italic text-paper text-[12px] mt-2">
                Será usado como rosto-âncora nos criativos. A IA gera baseado na persona + público + paleta.
              </p>
            </Section>
          </div>

          {/* PALETA */}
          <Section title="Paleta">
            <div className="grid grid-cols-3 gap-3">
              {(["primary","secondary","accent"] as const).map(k => (
                <div key={k}>
                  <div className="tag text-graydk mb-1">{k}</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={eff[k]}
                      onChange={(e) => patch({ [k]: e.target.value } as any)}
                      className="w-12 h-12 border-2 border-ink cursor-pointer"
                    />
                    <input
                      type="text"
                      value={eff[k]}
                      onChange={(e) => patch({ [k]: e.target.value } as any)}
                      className="flex-1 border border-ink bg-bone px-2 py-1 font-mono text-[12px] uppercase"
                    />
                  </div>
                  {ov[k] && (
                    <button onClick={() => clearField(clientId, k)} className="tag text-red underline mt-1">
                      reset → original ({client.brand[k]})
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* TEXTOS — todos com botão IA */}
          <TextField
            title="Pitch da empresa"
            field="pitch" value={eff.pitch}
            placeholder={client.pitch}
            onChange={(v) => patch({ pitch: v })}
            onGen={() => genText("pitch")}
            onClear={() => clearField(clientId, "pitch")}
            loading={loading === "pitch"}
            hasOverride={!!ov.pitch}
            rows={2}
          />

          <TextField
            title="Desafio principal"
            field="challenge" value={eff.challenge}
            placeholder={client.challenge}
            onChange={(v) => patch({ challenge: v })}
            onGen={() => genText("challenge")}
            onClear={() => clearField(clientId, "challenge")}
            loading={loading === "challenge"}
            hasOverride={!!ov.challenge}
            rows={2}
          />

          <TextField
            title="Tags (csv)"
            field="tags" value={eff.tagsCsv}
            placeholder={client.tags.join(", ")}
            onChange={(v) => patch({ tagsCsv: v })}
            onGen={() => genText("tags")}
            onClear={() => clearField(clientId, "tagsCsv")}
            loading={loading === "tags"}
            hasOverride={!!ov.tagsCsv}
          />

          <TextField
            title="Persona (cliente ideal)"
            field="persona" value={eff.persona ?? ""}
            placeholder="Empresário 35-50, fatura 7 dígitos, busca estruturação internacional. Dor: medo de exposição patrimonial."
            onChange={(v) => patch({ persona: v })}
            onGen={() => genText("persona")}
            onClear={() => clearField(clientId, "persona")}
            loading={loading === "persona"}
            hasOverride={!!ov.persona}
          />

          <TextField
            title="Público-alvo (refinado)"
            field="audience" value={eff.audience}
            placeholder={client.audience}
            onChange={(v) => patch({ audience: v })}
            onGen={() => genText("audience")}
            onClear={() => clearField(clientId, "audience")}
            loading={loading === "audience"}
            hasOverride={!!ov.audience}
          />

          <TextField
            title="Tipografia padrão"
            field="font" value={eff.font ?? ""}
            placeholder="Big Shoulders Display (display) + Inter (corpo) — combinação peso × leitura"
            onChange={(v) => patch({ font: v })}
            onGen={() => genText("font")}
            onClear={() => clearField(clientId, "font")}
            loading={loading === "font"}
            hasOverride={!!ov.font}
          />

          <TextField
            title="Tom de voz"
            field="voiceTone" value={eff.voiceTone ?? ""}
            placeholder="Direto, confidencial, sem rodeios. Fala com o decisor, nunca com o subordinado."
            onChange={(v) => patch({ voiceTone: v })}
            onGen={() => genText("voiceTone")}
            onClear={() => clearField(clientId, "voiceTone")}
            loading={loading === "voiceTone"}
            hasOverride={!!ov.voiceTone}
          />

          <TextField
            title="Briefing geral"
            field="brief" value={eff.brief ?? ""}
            placeholder="Posicionamento, diferencial, fase atual, próximos 90 dias…"
            onChange={(v) => patch({ brief: v })}
            onGen={() => genText("brief")}
            onClear={() => clearField(clientId, "brief")}
            loading={loading === "brief"}
            hasOverride={!!ov.brief}
            rows={4}
          />

          <div className="flex justify-end gap-2 pt-3 border-t border-ink/30">
            <button onClick={() => {
              if (confirm("Resetar TODOS os overrides deste cliente? Volta ao brand kit original.")) {
                useClientBrand.getState().clear(clientId);
              }
            }} className="btn btn-fire">reset tudo</button>
            <button onClick={onClose} className="btn btn-hire">salvo</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, actionable }:
  { title: string; children: React.ReactNode; actionable?: boolean }) {
  return (
    <section className="border-2 border-ink p-4 bg-bone">
      <div className="font-display font-bold uppercase text-base mb-2">
        {title} {actionable && <span className="tag text-graydk">· upload ou ✦ IA</span>}
      </div>
      {children}
    </section>
  );
}

function Preview({ src, placeholder }: { src?: string; placeholder: string }) {
  return src ? (
    <div className="w-24 h-24 border-2 border-ink bg-bone p-1 flex items-center justify-center shrink-0">
      <img src={src} alt="" className="max-w-full max-h-full object-contain" />
    </div>
  ) : (
    <div className="w-24 h-24 border-2 border-dashed border-graydk flex items-center justify-center
                    font-mono text-[10px] text-graydk text-center px-2 shrink-0">
      {placeholder}
    </div>
  );
}

function TextField(props: {
  title: string; field: string; value: string; placeholder: string;
  onChange: (v: string) => void; onGen: () => void; onClear: () => void;
  loading: boolean; hasOverride: boolean; rows?: number;
}) {
  return (
    <section className="border-2 border-ink p-4 bg-bone">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-bold uppercase text-base">{props.title}</div>
        <div className="flex gap-2">
          {props.hasOverride && (
            <button onClick={props.onClear} className="tag text-red underline">limpar</button>
          )}
          <button onClick={props.onGen} disabled={props.loading} className="btn btn-fire">
            {props.loading ? "gerando…" : "✦ gerar com IA"}
          </button>
        </div>
      </div>
      <textarea
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        rows={props.rows ?? 3}
        className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[13px] resize-y"
      />
    </section>
  );
}
